<template>
	<div>
		<div class="welcome-login text-center">
			<!-- display the district info -->
			<img v-if="logoSrc" :src="logoSrc" :alt="districtName + ' logo'" />
			<!-- h2, appended ": Log In" to be more specific, avoids skipping h1 -> h3 -->
			<h2 class="district-name" ref="pageTitle">{{ districtName }} Portal: Log In</h2>
			<hr />
			<!-- display any messages -->
			<!-- formFeedback inside the form (below), added role="alert" and aria-live for ARIA announcements -->
			<div
				id="formFeedback"
				v-if="!isTrustedClient"
			>
			<!-- h2 to avoid heading skip -->
			<h2>Log in to continue to <br/> <small>{{clientName}}</small></h2>
			</div>
		</div>

		<form
			method="post"
			ref="form"
			@submit.prevent="validateBeforeSubmit"
			:action="endpoint"
		>
			<!-- error alert inside form; added role="alert" aria-live="assertive" for ARIA announcements; added tabindex="-1" ref so we can move focus here -->
			<div
				v-if="!!getActiveMessage.content"
				id="formFeedback"
				:class="getMessageClass"
				v-html="getActiveMessage.content"
				v-show="!captchaRendered"
				role="alert"
				aria-live="assertive"
				ref="errorAlert"
				tabindex="-1"
			></div>

			<div v-if="hasAtleastOneSso && !parentLoginClicked">
				<button @click="parentLoginClicked = true;" type="button" class="btn btn-md btn-block btn-primary">
					Parent Login
				</button>
				<button @click="oidc()" type="button" :class="'btn btn-md btn-block ' + studentButtonColor">
					{{studentButtonText}}
				</button>
				<br/>
			</div>
			<input type="hidden" name="_csrftoken" :value="csrfToken" />

			<div
				class="form-group"
				:class="{ 'has-warning': errors.has('username') }"
				v-show="!showPin && showLogin && !captchaRendered"
			>
				<!-- label for="username-input" matches input id; added aria-required; added aria-describedby for inline error -->
				<label for="username-input">
					Username <span aria-hidden="true">*</span>
				</label>
				<input
					:type="!showPin ? 'text' : 'hidden'"
					class="form-control"
					id="username-input"
					name="username"
					v-model="username"
					v-validate="'required'"
					:autocomplete="!showPin ? 'username' : 'new-password'"
					aria-required="true"
					:aria-describedby="errors.has('username') ? 'username-error' : undefined"
				/>
				<!-- id for aria-describedby association; inline error per WCAG 3.3.1/3.3.3 -->
				<div v-show="errors.has('username')" id="username-error" role="alert">
					<small class="invalid-feedback">{{
						errors.first("username")
					}}</small>
				</div>
			</div>

			<div
				class="form-group"
				:class="{ 'has-warning': errors.has('password') }"
				v-show="!showPin && showLogin && !captchaRendered"
			>
				<!-- label for="password-input" matches input id; added aria-required; added aria-describedby for inline error -->
				<label for="password-input">
					Password <span aria-hidden="true">*</span>
				</label>
				<input
					:type="!showPin ? 'password' : 'hidden'"
					class="form-control"
					id="password-input"
					name="password"
					v-model="password"
					v-validate="'required'"
					autocomplete="current-password"
					aria-required="true"
					:aria-describedby="errors.has('password') ? 'password-error' : undefined"
				/>
				<!-- id for aria-describedby association; inline error per WCAG 3.3.1/3.3.3 -->
				<div v-show="errors.has('password')" id="password-error" role="alert">
					<small class="invalid-feedback">{{
						errors.first("password")
					}}</small>
				</div>
			</div>

			<!-- reCAPTCHA -->
			<div id="captchaDialog" class="modal" style="display:none;">
				<div class="modal-content">
					<div id="recaptcha-container">
						<iframe width="100%" height="600" style="border:none;" v-if="shouldShowCaptcha == 'Y'" :src="`${captchaHost}/Sapphire/captchaAPI/?captchaToken=${captchaToken}`" ref="myFrame" />
					</div>
				</div>
			</div>

			<!--  aria-hidden="true" and tabindex="-1" to fully exclude honeypot from tab order and screen readers -->
			<div style="position:absolute; left:-9999px;" aria-hidden="true" v-show="!showPin && showLogin">
				<div>
					<div>User Pin Code</div>
					<div>
						<input type="text" name="SECRERT_PIN" id="SECRERT_PIN" value="" autocomplete="one-time-code" tabindex="-1">
					</div>
				</div>
			</div>

			<div class="hidden">
				<input type="hidden" name="recaptchaResponse" v-model="recaptchaResponse" />
			</div>

			<div
				v-if="showPin"
				class="form-group"
				:class="{ 'has-warning': errors.has('pin') }"
			>
				<!-- label for="pin-input" matches input id -->
				<label for="pin-input">
					PIN
				</label>
				<input
					type="password"
					class="form-control"
					id="pin-input"
					name="pin"
					v-model="pin"
					maxlength="5"
					v-validate="'required'"
					autocomplete="none"
				/>

				<div v-show="errors.has('pin')">
					<small class="invalid-feedback">{{
						errors.first("pin")
					}}</small>
				</div>
			</div>

			<!-- label for="rememberMe-input" matches input id; added explanation text per WCAG 1.3.1 -->
			<div class="text-right form-group" v-if="allowRememberMe" v-show="!showPin">
				<p v-if="!showPin || rememberMe">
					<label for="rememberMe-input">
						Remember me?
						<span class="sr-only"> (keeps you logged in; do not use on publicly shared devices)</span>
					</label>
					<input type="checkbox" id="rememberMe-input" name="rememberMe" v-model="rememberMe" :true-value="true" :false-value="false"/>
				</p>
			</div>

			<div v-if="showLogin && hasAtleastOneSso && parentLoginClicked">
				<a :href="entrypoint + '/security/login/?status=reset'"
					><i class="fas fa-chevron-left"></i> Go Back</a
				>
			</div>

			<!-- secondary links moved BEFORE submit button so submit is last per WCAG 1.3.2 -->
			<div class="secondary-links" v-if="!showPin">
				<a v-if="showForgotPass && showLogin" :href="entrypoint + '/forgot-password'">Forgot your password?</a> <br>
				<a :href="entrypoint + '/district-check'">Apply for a Sapphire Community Portal account</a>
			</div><br/>

			<!--submit button now comes after secondary links -->
			<div class="" v-if="showLogin" v-show="!captchaRendered">
				<button
					type="submit"
					class="btn btn-block btn-primary"
				>
					<span v-if="!showPin">Login</span>
					<span v-else>Continue</span>
				</button>
			</div><br/>

			<hr/>
		</form>

		<div class="alert alert-info" v-html="registrationMessage" v-if="!showPin && isTrustedClient"></div>

		<template>
			<div class="" v-if="!showPin && isTrustedClient">
				<h3
					class="text-center"
					style="margin-bottom: 15px;"
					v-if="showRegister"
				>
					New Student Registration
				</h3>
				
					<a href="/CommunityWebPortal/Registration/login.cfm"
					class="btn btn-block btn-info"
					v-if="showRegister"
					>Register a new student</a>
				
			</div>

			<div class="go-back" v-if="showGoBack">
				<a :href="entrypoint + '/security/login/?status=logout'"
					><i class="fas fa-chevron-left"></i> Go Back</a
				>
			</div>
		</template>
	</div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';

export default {
	props: {
		districtName: "",
		logoSrc: "",
		clientName: {
			type: String,
			required: false,
			default: "Third Party Site",
		},
		csrfToken: "",
		message: {
			type: Object,
			default: function() {
				return {
					category: "info",
					content: "Welcome!"
				};
			}
		},
		hasAtleastOneSso: {
			type: Boolean,
			default: false
		},
		azureEnabled: {
			type: Boolean,
			default: false
		},
		oktaEnabled: {
			type: Boolean,
			default: false
		},
		googleEnabled: {
			type: Boolean,
			default: false
		},
		registrationMessage: "",
		showGoBack: {
			type: Boolean,
			default: false
		},
		showPin: {
			type: Boolean,
			default: false
		},
		showForgotPass: {
			type: Boolean,
			default: false
		},
		showRegister: {
			type: Boolean,
			default: false
		},
		allowRememberMe: {
			type: Boolean,
			default: true
		},
		propRememberMe:{
			type: Boolean,
			default: false,
		},
		entrypoint: {
			type: String,
			required: true
		},
		postEndpoint: {
			type: String,
			required: true
		},
		ssoEndpoint: {
			type: String,
			required: true
		},
		redirectUri: {
			type: String, 
			required: false,
			default: ""
		},
		j_username: {
			type: String,
			required: false
		},
		j_password: {
			type: String,
			required: false
		},
		studentButtonText: {
			type: String,
			required: false,
			default:"Student Login"
		},
		studentButtonColor: {
			type: String,
			required: false,
			default:"btn-primary"
		},
		trustedClientFlg: {
			type: String,
			required: false,
			default:"N"
		},
		captchaHost:{
			type: String,
			required: false,
			default:"https://sapphire.k12system.com"
		},

	},
	data() {
		return {
			username: "",
			password: "",
			pin: "",
			rememberMe: false,
			endpoint: this.postEndpoint,
			parentLoginClicked: false,
			recaptchaResponse:"",
			isProcessing:false,
			captchaRendered:false,
			shouldShowCaptcha: "N",
			captchaToken:""
		};
	},
	computed: {
		...mapGetters({
			vuexSessionExpiresAt: 'sessionExpiresAt',
		}),
		showCaptcha(){
			return (this.shouldShowCaptcha == 'Y') && (!this.showPin && this.showLogin) && this.captchaToken.length > 0;
		},
		isTrustedClient(){
			return this.trustedClientFlg == 'Y';
		},
		showLogin(){
			return !this.hasAtleastOneSso || this.parentLoginClicked || this.showPin;
		},
		isFormInvalid() {
			return Object.keys(this.fields).some(
				key => this.fields[key].invalid
			);
		},
		isFormTouched() {
			return Object.keys(this.fields).some(
				key => this.fields[key].touched
			);
		},
		getMessageClass() {
			return "alert alert-" + this.getActiveMessage.category;
		},
		getActiveMessage() {
			if (this.showPin) {
				return {
					category: "warning",
					content: "Please enter your PIN to continue."
				};
			} else if (this.isFormInvalid && this.isFormTouched) {
				return {
					category: "warning",
					content: "Some required fields are missing!"
				};
			} else {
				return this.message;
			}
		}
	},
	methods: {
		...mapMutations(['setSessionExpiresAt']),
		oidc(){
			this.endpoint = this.ssoEndpoint;
			setTimeout(()=>{
				this.$refs.form.submit();
			},1000);
			
		},
		validateBeforeSubmit() {
			if (this.isProcessing) {
				return false;
			}
			this.isProcessing = true;
			setTimeout(() => {
				this.isProcessing = false;
			},2000);
			this.checkForCaptcha().then((captchaResult) => {
				this.$validator.validateAll().then(result => {
					if (!result) {
						this.$nextTick(() => {
							if (this.$refs.errorAlert) {
								this.$refs.errorAlert.focus();
							}
						});
						this.isProcessing = false;
						return;
					}
					if (captchaResult != "Y" && !this.showCaptcha) {
						this.$refs.form.submit();
						return;
					}
					this.openCaptchaDialog();
				});
			});
		},
		openCaptchaDialog() {
			// Display the modal
			document.getElementById('captchaDialog').style.display = "block";
			document.getElementById('captchaDialog').style.position = "relative";
			document.querySelector(".modal-content").style.border="none";
			this.captchaRendered = true;
			
		},
		closeCaptchaDialog() {
			document.getElementById('captchaDialog').style.display = "none";
			this.captchaRendered = false;
		},
		checkForCaptcha() {
			return new Promise((resolve, reject) => {
				// No username? Skip AJAX and resolve immediately
				if (this.username.length === 0) {
					this.shouldShowCaptcha = "N";
					return resolve(false);
				}

				const payload = { username: this.username };
				const _self = this;
				$.get("/CommunityWebPortal/_auth/index.cfm/Security/useCaptchaFlg", payload, null, 'json')
					.done(function(data) {
						_self.shouldShowCaptcha = !!data.captchaEnabled ? data.captchaEnabled : "N";
						_self.captchaToken = !!data.captchaToken ? data.captchaToken : "";
						resolve(_self.shouldShowCaptcha);
					})
					.fail(function(err) {
						//fallback behavior (e.g., assume CAPTCHA is off)
						resolve("N"); // or reject(err) if you want it to error out
					});
			});
		}

	},
	watch: {
		vuexSessionExpiresAt:{
			handler(v){
				const expiration = new Date(v);
				let currentDateTime = new Date();
				const remainingTimeInSession = Math.max(0, expiration - currentDateTime);

				if( remainingTimeInSession > 0 ){
					location.reload();
				}
			},
			immediate: true
		},
	},
	mounted() {
		// If we hit the login component then auth server handlers say we are logged out.
		// Set expiration in vuex to 0 and ensure we are sync between auth and app.
		this.setSessionExpiresAt(0);

		if (this.showPin) {
			this.username = this.j_username;
			this.password = this.j_password;
			this.rememberMe = this.propRememberMe;
		}
		
		
		this.$nextTick(()=> {
			/* Load frame */
			const root = this;
			window.addEventListener('message', function(event) {
				// Check the origin of the message for security
				if (event.origin !== '') {
					//return; // Ignore messages from untrusted origins
				}
				console.log(event.origin);
				// Process the message
				if (!!event.data && typeof event.data == 'string' && event.data.substring(0,8) == "captcha:") {
					console.log(event.data);
					
					root.recaptchaResponse = event.data.replace("captcha:", "");
					root.$validator.validateAll().then(result => {
						root.$refs.form.submit();
						return;
					});
				}
			});
		});
	}
};
</script>

<style scoped>
/* Basic modal styling */
.modal {
    position: relative !important;
	left: 50%;
	top: 50%;
	transform: translate(-50%,-50%);
	width: 50vw;
	min-height: 50vh;
	z-index: 99;
	box-shadow: 0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23);
	display: none;
	padding: 15px;
}

.modal-content {
    position: relative;
  	min-height: 40vh;

}

.close {
    color: #aaa;
    float: right;
    font-size: 28px;
    font-weight: bold;
}

.close:hover,
.close:focus {
    color: black;
    text-decoration: none;
    cursor: pointer;
}
</style>